import React, { useEffect } from 'react';
import { CheckboxUI, DebouncedInputUI, StaticDatePickerUI } from '../Interface';
import dayjs from 'dayjs';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view-pro';
import { Box, Typography } from '@mui/material';
import useUsers from '../../context/UsersContext';
import useSaveView from '../../hooks/useSaveView';
import { CalendarType } from '../../config/taskCalendar';

const CalendarFilter = ({ dates, resources, queryState, hasResourceFilter, roles, calendarTypeState }) => {
  const { setCalendarType, calendarType } = calendarTypeState;
  const { user } = useUsers();
  const saveView = useSaveView(user);
  const { date, setDate } = dates;
  const { query, setQuery } = queryState;

  useEffect(() => {
    if (roles.TASK_CALENDAR_SEE_ONLY_OWN && user?.userId) {
      setResponsibleIds([user.userId]);
    }
  }, [roles.TASK_CALENDAR_SEE_ONLY_OWN, user?.userId]);

  const items = query.filter?.filterModel?.items ?? [];
  const FIELD = 'responsiblePersonId';

  const setResponsibleIds = (ids) => {
    setQuery((prev) => {
      const prevItems = prev.filter?.filterModel?.items ?? [];
      const newItems = prevItems.filter((i) => i.field !== FIELD);

      if (ids.length > 0) {
        newItems.push({ field: FIELD, operator: 'isAnyOf', value: ids });
      }

      return {
        ...prev,
        filter: {
          ...prev.filter,
          filterModel: {
            ...(prev.filter?.filterModel ?? {}),
            items: newItems,
            logicOperator: 'and',
          },
        },
      };
    });
  };

  const getFieldItem = (field) => items.find((item) => item.field === field);

  const setFieldItem = (field, value, operator = 'is') => {
    setQuery((prev) => {
      const prevItems = prev.filter?.filterModel?.items ?? [];
      const newItems = prevItems.filter((i) => i.field !== field);

      if (value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
        newItems.push({
          field,
          operator,
          value,
        });
      }

      return {
        ...prev,
        filter: {
          ...prev.filter,
          filterModel: {
            ...(prev.filter?.filterModel ?? {}),
            items: newItems,
            logicOperator: 'and',
          },
        },
      };
    });
  };

  const isChecked = (id) => {
    const item = getFieldItem(FIELD);
    if (!item || !Array.isArray(item.value)) return false;
    return item.value.map(String).includes(String(id));
  };

  const handleToggle = (id) => {
    if (roles.TASK_CALENDAR_SEE_ONLY_OWN) return;

    const item = getFieldItem(FIELD);
    const current = Array.isArray(item?.value) ? item.value : [];
    const exists = current.map(String).includes(String(id));
    const updated = exists ? current.filter((v) => String(v) !== String(id)) : [...current, id];

    setResponsibleIds(updated);
  };

  const renderCheckLabel = (id, label) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
      <CheckboxUI
        sx={{ m: 0, p: 0 }}
        checked={isChecked(id)}
        onChange={() => handleToggle(id)}
        disabled={roles.TASK_CALENDAR_SEE_ONLY_OWN || !calendarType.length}
      />
      <Typography>{label}</Typography>
    </Box>
  );

  const handleSelectType = (type) => {
    const next = calendarType.includes(type) ? calendarType.filter((x) => x !== type) : [...calendarType, type];
    setCalendarType(next);
    saveView('taskCalendarFilter', { calendarTypes: next });
  };

  return (
    <div>
      <div className="w-full">
        <StaticDatePickerUI
          sx={{
            '& .MuiDatePickerToolbar-root': { display: 'none' },
            '& .MuiDialogActions-root': { display: 'none' },
            ' & .MuiDateCalendar-root': { background: '#f3f4f6' },
          }}
          value={dayjs(date)}
          onChange={(newDate) => {
            setDate((prevState) => ({
              ...prevState,
              startDate: newDate ? newDate.startOf('day') : null,
              endDate: newDate ? newDate.endOf('day') : null,
            }));
          }}
        />
      </div>

      <Box ml={1} mb={1}>
        <DebouncedInputUI
          size="small"
          placeholder="Projekt keresés…"
          debounceMs={800}
          variant="outlined"
          setQuickFilterSearchValue={(newValue) => setFieldItem('projectName', newValue, 'contains')}
          quickFilterSearchValue={getFieldItem('projectName')?.value}
          disabled={!hasResourceFilter}
        />
      </Box>

      <Box ml={1} mb={1}>
        <Box>
          <CheckboxUI
            sx={{ m: 0, p: 0 }}
            label="Projekt feladatlista"
            onChange={() => handleSelectType(CalendarType.TASKLIST)}
            checked={calendarType.includes(CalendarType.TASKLIST)}
          />
        </Box>
        <Box>
          <CheckboxUI
            sx={{ m: 0, p: 0 }}
            label="Feladatok"
            onChange={() => handleSelectType(CalendarType.SIMPLETASK)}
            checked={calendarType.includes(CalendarType.SIMPLETASK)}
          />
        </Box>
      </Box>

      <SimpleTreeView>
        <TreeItem itemId="responsibles-root" label="Személyek">
          {resources?.user?.map((user) => (
            <TreeItem
              key={`resp-${user.userId}`}
              itemId={`resp-${user.userId}`}
              label={renderCheckLabel(user.userId, user.nickName)}
            />
          ))}
        </TreeItem>
      </SimpleTreeView>
    </div>
  );
};

export default CalendarFilter;
